import React from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import {useIntl} from 'react-intl';
// Import Shared Components
import { ButtonGroup, Footer } from 'shared-components';
import useWhiteLabeling from 'shared-components/src/hooks/useWhiteLabeling';
import { getCardholderSupportEmail } from 'shared-components/src/utils/whitelabel';
// Import Global Components
import Title from '../../../../components/Title';
import Heading from '../../../../components/Heading';
import Alert from '../../../../components/Alert';
// Import Local Components
import TransferTypeButton from '../../components/TransferTypeButton';
// Import Utils
import { hasTransfersEnabled } from '../../../../utils/transfers';
// Import Component Styles
import {Section} from './styles';
import {Superscript, Text} from '../../components/TransferTypeButton/styles';
import { HeadingTabs, HeadingWrapper } from '../../styles';

const SelectTransfer = ({ configs }) => {
  const navigate = useNavigate();
  const { callCenterBanner } = useWhiteLabeling();
  const intl = useIntl();
  const page = 'transfer';

  return (
    <>
      { 
        configs.allow_etransfer_banner && (
          <Alert
            alert={intl.messages[`${page}-etransfer-issue-banner-alert`]}
            type="warn"
            alertFor="etransfer-issue-banner"
            page={page}
          />
        ) 
      }
      { 
        callCenterBanner && (
          <Alert
            // eslint-disable-next-line dot-notation
            alert={intl.messages['cardholder_support_email']}
            type="warn"
            alertFor="call-center-banner"
            values={{
              cardholder_support_email: getCardholderSupportEmail()
            }}
          />
        ) 
      }
      <HeadingWrapper>
        <HeadingTabs>
          <Heading page={page} heading={intl.messages[`${page}-heading`]} className="active" />
          <Link to="transfer-history">
            <Heading page='transfer-history' heading={intl.messages['transfer-history-heading']} />
          </Link>
          {configs.allow_external_transfers_v2 && (
            <Link to="external-accounts">
              <Heading page='external-accounts' heading={intl.messages['external-accounts-heading']} />
            </Link>
          )}
        </HeadingTabs>
      </HeadingWrapper>
      {!hasTransfersEnabled(configs) ? (
        <Alert
          alert={intl.messages[`${page}-not-enabled-alert`]}
          type="info"
          alertFor="not-enabled"
          page={page}
        />
      ) : (
        <>
          {configs.allow_personal_bank_account_transfers && (
            <Section>
              <Title
                title={intl.messages[`${page}-make-transfer-title`]}
                titleFor="make-transfer"
                page={page}
              />
              <ButtonGroup justifyContent="flex-start">
                <TransferTypeButton
                  text={intl.messages[`${page}-to-my-account-text`]}
                  textFor="to-my-account"
                  onClick={e => {
                    e.preventDefault();
                    navigate('my-account');
                  }}
                />
              </ButtonGroup>
            </Section>
          )}
          {(configs.allow_account_to_account_transfer ||
            configs.allow_child_cards ||
            configs.allow_person_to_person_transfers ||
            configs.allow_external_transfers_v2) && (
            <Section>
              <Title
                title={intl.messages[`${page}-send-money-title`]}
                titleFor="send-money"
                page={page}
              />
              <ButtonGroup justifyContent="flex-start">
                {(configs.allow_account_to_account_transfer ||
                  configs.allow_child_cards) && (
                  <TransferTypeButton
                    text={intl.messages[`${page}-account-to-account-text`]}
                    textFor="card-to-card"
                    onClick={e => {
                      e.preventDefault();
                      navigate('card-to-card');
                    }}
                  />
                )}
                {configs.allow_person_to_person_transfers && (
                  <TransferTypeButton
                    text={intl.messages[`${page}-person-to-person-text`]}
                    textFor="person-to-person"
                    onClick={e => {
                      e.preventDefault();
                      navigate('person-to-person');
                    }}
                  />
                )}
                {configs.allow_external_transfers_v2 && (
                  <>
                    <TransferTypeButton
                      text={intl.messages[`${page}-etransfer-push-text`]}
                      textFor="etransfer-push"
                      disabled={!configs.allow_etransfer_push_v2}
                      style={configs.allow_etransfer_push_v2 ? {} : { opacity: 0.5, cursor: 'not-allowed' }}
                      onClick={e => {
                        e.preventDefault();
                        if (configs.allow_etransfer_push_v2) navigate('etransfer-push');
                      }}
                    />
                    <TransferTypeButton
                      text={intl.messages[`${page}-directsend-push-text`]}
                      textFor="directsend-push"
                      onClick={e => {
                        e.preventDefault();
                        navigate('directsend-push');
                      }}
                    />
                  </>
                )}
              </ButtonGroup>
            </Section>
          )}
          {(configs.allow_interac_pull_transfers || configs.allow_external_transfers_v2) && (
            <Section>
              <Title
                title={intl.messages[`${page}-request-money-title`]}
                titleFor="request-money"
                page={page}
              />
              <ButtonGroup justifyContent="flex-start">
                {configs.allow_external_transfers_v2 && (
                  <>
                    <TransferTypeButton
                      text={intl.messages[`${page}-etransfer-pull-text`]}
                      textFor="etransfer-pull"
                      onClick={e => {
                        e.preventDefault();
                        navigate('etransfer-pull');
                      }}
                    />
                    <TransferTypeButton
                      text={intl.messages[`${page}-directsend-pull-text`]}
                      textFor="directsend-pull"
                      onClick={e => {
                        e.preventDefault();
                        navigate('directsend-pull');
                      }}
                    />
                  </>
                )}
                {configs.allow_interac_pull_transfers && (
                  <TransferTypeButton
                    text={intl.messages[`${page}-person-to-external-text`]}
                    textFor="person-to-external"
                    onClick={e => {
                      e.preventDefault();
                      navigate('person-to-external');
                    }}
                  />
                )}
              </ButtonGroup>
            </Section>
          )}
        </>
      )}
      {configs.allow_external_transfers_v2 && (
        <Footer
          legalStatements={(
            <Text
              text={intl.messages['transfer-etransfer-footnote-text']}
              textFor='etransfer-footnote'
              values={{
                registerSuperscript: <Superscript>®</Superscript>,
                registerFooterNote: '‡',
              }}
              page='transfer'
            />
          )}
        />
      )}
    </>
  );
};

SelectTransfer.propTypes = {
  configs: PropTypes.shape({
    allow_personal_bank_account_transfers: PropTypes.bool.isRequired,
    allow_person_to_person_transfers: PropTypes.bool.isRequired,
    allow_account_to_account_transfer: PropTypes.bool.isRequired,
    allow_child_cards: PropTypes.bool.isRequired,
    allow_interac_pull_transfers: PropTypes.bool.isRequired,
    allow_external_transfers_v2: PropTypes.bool.isRequired,
    allow_etransfer_banner: PropTypes.bool.isRequired,
    allow_etransfer_push_v2: PropTypes.bool.isRequired,
  }).isRequired,
};

export default SelectTransfer;
